import { create } from "zustand";
import PartnerProfile, {
  OpeningTimes,
  Photo,
  PartnerMassageService,
  PartnerWorker,
} from "../../user/partner/PartnerProfile";
import { GenderTypeKey } from "../../user/partner/Gender";
import { WorkplaceKey } from "../../user/partner/Workplace";
import { AvailabilityKey } from "../../user/partner/Availability";
import AddPartnerProfileFormWorker from "../../user/partner/AddPartnerProfileFormWorker";
import { MassageCategoryKey } from "../../user/partner/MassageCategoryKey";
import { ClientTypeKey } from "../../user/client/ClientType";
import { PaymentMethodKey } from "../../user/partner/PaymentMethod";
import { useAddPartnerStore } from "./addPartnerStore";

interface EditPartnerProfileStore {
  existingPhotos?: Photo[];
  setExistingPhotos: (photos: Photo[]) => void;
  photosToAdd?: Photo[];
  setPhotosToAdd: (photosToAdd: Photo[]) => void;
  photosToDelete?: Photo[];
  setPhotosToDelete: (photosToDelete: Photo[]) => void;
  name?: string;
  setName: (name: string) => void;
  gender?: GenderTypeKey;
  setGender: (gender: GenderTypeKey) => void;
  description?: string;
  setDescription: (description: string) => void;
  engDescription?: string;
  setEngDescription: (engDescription: string) => void;
  city?: string;
  setCity: (city: string) => void;
  cityDescription?: string;
  setCityDescription: (cityDescription: string) => void;
  district?: string;
  setDistrict: (district: string) => void;
  neighbourhood?: string;
  setNeighbourhood: (neighbourhood: string) => void;
  street?: string;
  setStreet: (street: string) => void;
  houseNumber?: string;
  setHouseNumber: (houseNumber: string) => void;
  postCode?: string;
  setPostCode: (postCode: string) => void;
  isVisitingClient?: boolean;
  setIsVisitingClient: (isVisitingClient: boolean) => void;
  workplace?: WorkplaceKey;
  setWorkplace: (workplaceKey: WorkplaceKey) => void;
  availability?: AvailabilityKey[];
  setAvailability: (availability: AvailabilityKey[]) => void;
  openingTimes?: OpeningTimes;
  setOpeningTimes: (openingTimes: OpeningTimes) => void;

  workersToAdd?: AddPartnerProfileFormWorker[];
  setWorkersToAdd: (workers?: AddPartnerProfileFormWorker[]) => void;
  workersToDelete?: AddPartnerProfileFormWorker[];
  setWorkersToDelete: (workers?: AddPartnerProfileFormWorker[]) => void;
  workersToEdit?: AddPartnerProfileFormWorker[];
  setWorkersToEdit: (workers?: AddPartnerProfileFormWorker[]) => void;
  existingWorkers?: PartnerWorker[];
  setExistingWorkers: (photos: PartnerWorker[]) => void;

  serviceCategory?: MassageCategoryKey;
  setServiceCategory: (serviceCategory: MassageCategoryKey) => void;
  services?: PartnerMassageService[];
  setServices: (services?: PartnerMassageService[]) => void;
  currency?: string;
  setCurrency: (currency: string) => void;
  isTechnician?: boolean;
  setIsTechnician: (isTechnician: boolean) => void;
  clientTypes?: ClientTypeKey[];
  setClientType: (type: ClientTypeKey[]) => void;
  paymentMethods?: PaymentMethodKey[];
  setPaymentMethod: (paymentMethod: PaymentMethodKey[]) => void;
  phonePrefix?: string;
  setPhonePrefix: (phonePrefix: string) => void;
  phoneNumber?: string;
  setPhoneNumber: (phoneNumber: string) => void;
  email?: string;
  setEmail: (email: string) => void;
  website?: string;
  setWebsite: (website: string) => void;
  facebook?: string;
  setFacebook: (facebook: string) => void;
  instagram?: string;
  setInstagram: (instagram: string) => void;
  youtube?: string;
  setYoutube: (youtube: string) => void;
  breakTime?: string;
  setBreakTime: (breakTime: string) => void;
  isCalendarEnabled: boolean;
  setIsCalendarEnabled: (isCalendarEnabled: boolean) => void;
}

export const useEditPartnerStore = create<EditPartnerProfileStore>((set) => ({
  existingPhotos: [],
  setExistingPhotos: (existingPhotos) => set({ existingPhotos }),

  photosToAdd: [],
  setPhotosToAdd: (photosToAdd) => set({ photosToAdd }),

  photosToDelete: [],
  setPhotosToDelete: (photosToDelete) => set({ photosToDelete }),

  name: "",
  setName: (name) => set({ name }),

  gender: undefined,
  setGender: (gender) => set({ gender }),

  description: "",
  setDescription: (description) => set({ description }),

  engDescription: "",
  setEngDescription: (engDescription) => set({ engDescription }),

  city: "",
  setCity: (city) => set({ city }),

  cityDescription: "",
  setCityDescription: (cityDescription) => set({ cityDescription }),

  district: "",
  setDistrict: (district) => set({ district }),

  neighbourhood: "",
  setNeighbourhood: (neighbourhood) => set({ neighbourhood }),

  street: "",
  setStreet: (street) => set({ street }),

  houseNumber: "",
  setHouseNumber: (houseNumber) => set({ houseNumber }),

  postCode: "",
  setPostCode: (postCode) => set({ postCode }),

  isVisitingClient: false,
  setIsVisitingClient: (isVisitingClient) => set({ isVisitingClient }),

  workplace: undefined,
  setWorkplace: (workplace) => set({ workplace }),

  availability: [],
  setAvailability: (availability) => set({ availability }),

  openingTimes: undefined,
  setOpeningTimes: (openingTimes) => set({ openingTimes }),

  workersToAdd: [],
  setWorkersToAdd: (workersToAdd) => set({ workersToAdd }),

  workersToDelete: [],
  setWorkersToDelete: (workersToDelete) => set({ workersToDelete }),

  workersToEdit: [],
  setWorkersToEdit: (workersToEdit) => set({ workersToEdit }),

  existingWorkers: [],
  setExistingWorkers: (existingWorkers) => set({ existingWorkers }),

  serviceCategory: undefined,
  setServiceCategory: (serviceCategory) => set({ serviceCategory }),

  services: [],
  setServices: (services) => set({ services }),

  currency: "",
  setCurrency: (currency) => set({ currency }),

  isTechnician: false,
  setIsTechnician: (isTechnician) => set({ isTechnician }),

  clientTypes: [],
  setClientType: (clientTypes) => set({ clientTypes }),

  paymentMethods: [],
  setPaymentMethod: (paymentMethods) => set({ paymentMethods }),

  phonePrefix: "",
  setPhonePrefix: (phonePrefix) => set({ phonePrefix }),

  phoneNumber: "",
  setPhoneNumber: (phoneNumber) => set({ phoneNumber }),

  email: "",
  setEmail: (email) => set({ email }),

  website: "",
  setWebsite: (website) => set({ website }),

  facebook: "",
  setFacebook: (facebook) => set({ facebook }),

  instagram: "",
  setInstagram: (instagram) => set({ instagram }),

  youtube: "",
  setYoutube: (youtube) => set({ youtube }),

  breakTime: "",
  setBreakTime: (breakTime) => set({ breakTime }),

  isCalendarEnabled: false,
  setIsCalendarEnabled: (isCalendarEnabled) => set({ isCalendarEnabled }),
}));

export const useEditPartnerStoreSetup = () => {
  const store = useEditPartnerStore();

  const setDefaultData = (partner?: PartnerProfile) => {
    if (!partner) return;

    store.setExistingPhotos(partner.photos || []);

    store.setName(partner.name);
    store.setGender(partner.gender);
    store.setDescription(partner.description);

    store.setEngDescription(partner.engDescription || "");
    store.setCity(partner.city || "");
    store.setCityDescription(partner.cityDescription || "");
    store.setDistrict(partner.district || "");
    store.setNeighbourhood(partner.neighbourhood || "");
    store.setStreet(partner.street || "");
    store.setHouseNumber(partner.houseNumber || "");
    store.setPostCode(partner.postCode || "");
    store.setIsVisitingClient(!!partner.isVisitingClient);
    store.setWorkplace(partner.workplace);
    store.setAvailability(partner.availability || []);
    store.setOpeningTimes(partner.openingTimes);
    store.setExistingWorkers(partner.workers || []);
    store.setServiceCategory(partner.serviceCategory);
    store.setServices(partner.services || []);
    store.setCurrency(partner.currency || "");
    store.setIsTechnician(partner.isTechnician);
    store.setClientType(partner.clientTypes || []);
    store.setPaymentMethod(partner.paymentMethods || []);
    store.setPhonePrefix(partner.phonePrefix || "");
    store.setPhoneNumber(partner.phoneNumber || "");
    store.setEmail(partner.email || "");
    store.setWebsite(partner.website || "");
    store.setFacebook(partner.facebook || "");
    store.setInstagram(partner.instagram || "");
    store.setYoutube(partner.youtube || "");
    store.setBreakTime(partner.breakTime?.toString() || "");
    store.setIsCalendarEnabled(!!partner?.isCalendarEnabled);
  };

  return { setDefaultData };
};
